/**
 * Application Shell — wraps all original working components with
 * Glass-Forward chrome (ContextualSideNav, TopBar, persona context).
 *
 * Auth uses the original auth.ts module; the actual network call is
 * apiv2.me() (see apiv2.ts), same-origin against ztd_app's /api/v2/*.
 * All routes import real components from src/components/.
 */

import { useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "../ThemeContext";
import { Providers, RouteAwareProviders } from "./providers";
import { AppRoutes } from "./routes";

// Some Windows mice map click-drag text selection to an extra button
// (button 3/4) that the browser also binds to back/forward history
// navigation. Reported by Windows users: dragging to select text inside
// a field (e.g. Schema name) can fire a full back-navigation mid-typing,
// silently discarding whatever form was in progress. Not reproducible on
// a trackpad/standard mouse, so scope the guard to text-entry targets
// only -- legitimate back/forward-button navigation elsewhere is untouched.
function guardAgainstAccidentalHistoryNav(e: MouseEvent) {
  if (e.button !== 3 && e.button !== 4) return;
  const target = e.target as HTMLElement | null;
  if (target?.closest("input, textarea, select, [contenteditable='true']")) {
    e.preventDefault();
  }
}

export function App() {
  useEffect(() => {
    window.addEventListener("mousedown", guardAgainstAccidentalHistoryNav);
    return () =>
      window.removeEventListener("mousedown", guardAgainstAccidentalHistoryNav);
  }, []);

  return (
    <BrowserRouter>
      <ThemeProvider>
        <Providers>
          <RouteAwareProviders>
            <AppRoutes />
          </RouteAwareProviders>
        </Providers>
      </ThemeProvider>
    </BrowserRouter>
  );
}
